import React from 'react'
import { stringify } from 'query-string'
import { useLocation } from 'react-router'

import ArtboardSubscriptionStatus from '../../components/ArtboardSubscriptionStatus'
import CommentTabMessageRestricted from '../../components/CommentTabMessageRestricted'

import { Share } from '../../types'
import { useVersioning } from '../../../versioning'
import { useQueryParams } from '@sketch/modules-common'
import { TooltipShortcut } from '@sketch/components'
import { shortcuts } from '@sketch/utils'

// This is a valid use case to narrow down Component type
// eslint-disable-next-line no-restricted-imports
import { NotificationSubscriptionStatus } from '@sketch/gql-types/expansive'
import {
  useAnnotationOverlayContext,
  usePlaceDraftAnnotation,
} from '../../hooks'
import { useStatus } from '@sketch-hq/sketch-web-renderer'

import { ReactComponent as ChatBubbleAddIcon } from '@sketch/icons/comment-chat-bubble-plus-17'
import { AddAnnotationButton } from './ArtboardAnnotations.styles'
import AnnotationListContainer from '../AnnotationListContainer'
import {
  ArtboardDetailInfoFragment,
  FrameDetailInfoFragment,
} from '@sketch/gql-types'
import VersionRestrictedWarning from '../../components/VersionRestrictedWarning'

interface ArtboardAnnotationsProps {
  share: Share
  permanentPageIdentifier: string
  frameGroup: ArtboardDetailInfoFragment | FrameDetailInfoFragment
  notificationStatus: NotificationSubscriptionStatus
  showAddAnnotationButton?: boolean
  permitViewingAnnotations?: boolean
}

const ArtboardAnnotations: React.FC<ArtboardAnnotationsProps> = props => {
  const {
    share,
    frameGroup,
    notificationStatus,
    showAddAnnotationButton,
    permitViewingAnnotations,
  } = props

  const canvasStatus = useStatus()
  const location = useLocation()
  const annotationContext = useAnnotationOverlayContext()

  const { latestVersion } = useVersioning()
  const [isPlacingAnnotation, setPlaceAnnotation] =
    usePlaceDraftAnnotation() || []

  const { annotation, ...otherParams } = useQueryParams<
    'ARTBOARD_DETAIL_UUID' | 'ARTBOARD_DETAIL' | 'FRAME'
  >()

  if (!permitViewingAnnotations) {
    return <VersionRestrictedWarning location={location} />
  }

  if (!share.commentsEnabled) {
    return <CommentTabMessageRestricted />
  }

  const artboardSubscriptionStatus = (
    <ArtboardSubscriptionStatus
      shareIdentifier={share.identifier}
      frameGroup={frameGroup}
      documentVersionShortId={latestVersion.shortId}
      notificationStatus={notificationStatus}
    />
  )

  const isCanvasLoading = canvasStatus?.type !== 'READY'

  return (
    <AnnotationListContainer
      isCanvasLoading={isCanvasLoading}
      resolveAnnotationLink={annotation => location => ({
        ...location,
        state: {
          isFromCommentTab: true,
        },
        search: stringify({
          ...otherParams,
          annotation: annotation.identifier,
        }),
      })}
      navbarAction={
        <>
          {artboardSubscriptionStatus}
          {showAddAnnotationButton && (
            <AddAnnotationButton
              onClick={(event: MouseEvent) => {
                setPlaceAnnotation?.(!isPlacingAnnotation)
                event.stopPropagation()
              }}
              aria-current={isPlacingAnnotation}
              disabled={
                isCanvasLoading || !annotationContext?.isViewingLatestVersion
              }
              description={
                <>
                  Add comment{' '}
                  <TooltipShortcut>
                    Press {shortcuts.addComment.toUpperCase()}
                  </TooltipShortcut>
                </>
              }
            >
              <span className="sr-only">New comment</span>
              <ChatBubbleAddIcon role="img" />
            </AddAnnotationButton>
          )}
        </>
      }
      activeAnnotationIdentifier={annotation}
    />
  )
}

export default ArtboardAnnotations
